/**
 * LatencyReporter (NO-7 client side) — periodically reads the local
 * MP-diagnostics snapshot (window.__HELIX_MP_DIAGNOSTICS__) and sends
 * a `clientPhaseSummary` to the server, which aggregates it into the
 * `netobs:latency_aggregate` broadcast that powers the heatmap panel.
 *
 * Mounted from main.tsx alongside the other spectator-style bridges.
 *
 * Gating: only mounts/runs when isNetobsEnabled() — saves the 0.2 Hz
 * room.send when nothing's reading the heatmap.
 */

import React, { useEffect } from 'react';
import { isNetobsEnabled } from './isNetobsEnabled';

const REPORT_INTERVAL_MS = 5000;

interface MpSnapshot {
  clockSync?: {
    ready?: boolean;
    rttMs?: number;
  };
  phaseTimings?: {
    sampleCount?: number;
    rollingMean?: { phase1Ms?: number; phase2Ms?: number };
  };
}

function readSnapshot(): MpSnapshot | null {
  return (globalThis as any).__HELIX_MP_DIAGNOSTICS__ ?? null;
}

function sendSummary(): void {
  const snap = readSnapshot();
  if (!snap) return;
  const room = (globalThis as any).nm?.getRoom?.();
  if (!room || typeof room.send !== 'function') return;
  const cs = snap.clockSync;
  const pt = snap.phaseTimings;
  // Skip the report if neither dataset is ready — server has nothing
  // useful to show, and the report would just clutter the packet log.
  const hasClockSync = cs?.ready === true;
  const hasPhaseData = (pt?.sampleCount ?? 0) > 0;
  if (!hasClockSync && !hasPhaseData) return;
  try {
    room.send('clientPhaseSummary', {
      phase1Ms: pt?.rollingMean?.phase1Ms ?? 0,
      phase2Ms: pt?.rollingMean?.phase2Ms ?? 0,
      rttMs: cs?.rttMs ?? 0,
      clockReady: hasClockSync,
    });
  } catch {
    // room may have torn down; ignore
  }
}

const LatencyReporterInner: React.FC = () => {
  useEffect(() => {
    // First report after one cadence — gives clockSync time to land
    // its first pong (~200ms after connect) and PhaseTimingTracker
    // a few frames of samples.
    const id = setInterval(sendSummary, REPORT_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);
  return null;
};

export const LatencyReporter: React.FC = () => {
  if (!isNetobsEnabled()) return null;
  return <LatencyReporterInner />;
};
